import React, { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCreditCard, faArrowLeft, faCalendar, faCheckCircle, faTimesCircle,
  faDollarSign, faFileInvoice, faSpinner, faReceipt, faClock, faChartLine,
  faArrowRight, faTag, faIdCard, faGraduationCap
} from '@fortawesome/free-solid-svg-icons';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { getExamUrl } from '../utils/slugify';

interface BillingRecord {
  id: number;
  exam_id: number;
  payment_id: string;
  amount_paid: number;
  currency: string;
  start_date: string;
  end_date: string;
  created_at: string;
  is_active: boolean;
  exam_name: string;
  exam_description: string;
}

const BillingHistory: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [billingHistory, setBillingHistory] = useState<BillingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [freeModeEnabled, setFreeModeEnabled] = useState(false);

  const isRTL = i18n.language === 'ar';
  const rtlDir = isRTL ? 'rtl' : 'ltr';

  useEffect(() => {
    fetchBillingHistory();
    fetchFreeModeStatus();
  }, []);

  const fetchFreeModeStatus = async () => {
    try {
      const response = await api.get('/settings/free-mode/status');
      setFreeModeEnabled(response.data.freeModeEnabled || false);
    } catch (error) {
      console.error('Failed to fetch free mode status:', error);
      setFreeModeEnabled(false);
    }
  };

  const fetchBillingHistory = async () => {
    try {
      const response = await api.get('/subscriptions/billing/history');
      setBillingHistory(response.data.billingHistory || []);
    } catch (error) {
      console.error('Failed to fetch billing history:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount || 0);
  };

  const getStatusBadge = (record: BillingRecord) => {
    const now = new Date();
    const endDate = new Date(record.end_date);

    if (!record.is_active) {
      return (
        <span
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-semibold ${isRTL ? 'flex-row-reverse' : ''}`}
          style={{ background: 'var(--gp-surface-muted)', color: 'var(--gp-ink-muted)', border: '1px solid var(--gp-border)' }}
        >
          <FontAwesomeIcon icon={faTimesCircle} className="text-xs" />
          <span>{t('billing.inactive')}</span>
        </span>
      );
    }

    if (endDate < now) {
      return (
        <span
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-semibold ${isRTL ? 'flex-row-reverse' : ''}`}
          style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}
        >
          <FontAwesomeIcon icon={faTimesCircle} className="text-xs" />
          <span>{t('billing.expired')}</span>
        </span>
      );
    }

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-semibold ${isRTL ? 'flex-row-reverse' : ''}`}
        style={{ background: 'var(--gp-success-soft)', color: 'var(--gp-success)', border: '1px solid var(--gp-success)' }}
      >
        <FontAwesomeIcon icon={faCheckCircle} className="text-xs" />
        <span>{t('billing.active')}</span>
      </span>
    );
  };

  const totalSpent = billingHistory.reduce((sum, record) => sum + (record.amount_paid || 0), 0);
  const activeSubscriptions = billingHistory.filter(record => {
    const endDate = new Date(record.end_date);
    return record.is_active && endDate > new Date();
  }).length;
  const totalPurchases = billingHistory.length;

  if (freeModeEnabled) {
    return <Navigate to="/dashboard" replace />;
  }

  if (loading) {
    return (
      <div className="gp-page flex items-center justify-center" dir={rtlDir}>
        <div className={isRTL ? 'text-right' : 'text-center'}>
          <div className="gp-spinner mb-3" />
          <p className="gp-text-muted text-sm">{t('billing.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gp-page py-6" dir={rtlDir}>
      <div className="gp-container">
        <Link
          to="/dashboard"
          className={`gp-back-link mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}
        >
          <FontAwesomeIcon icon={faArrowLeft} className="text-xs" style={{ transform: isRTL ? 'scaleX(-1)' : 'none' }} />
          {t('common.backToDashboard')}
        </Link>

        <div className="gp-page-header">
          <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="gp-icon-tile-lg">
              <FontAwesomeIcon icon={faCreditCard} className="text-lg" />
            </div>
            <div className={`min-w-0 flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
              <h1 className="gp-page-title">{t('billing.title')}</h1>
              <p className="gp-page-subtitle">{t('billing.subtitle')}</p>
            </div>
          </div>
        </div>

        {billingHistory.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            <div className="gp-stat text-left">
              <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={isRTL ? 'text-right' : 'text-left'}>
                  <div className="gp-stat-label">{t('billing.totalSpent')}</div>
                  <div className="gp-stat-value text-lg sm:text-xl">
                    {formatCurrency(totalSpent, billingHistory[0]?.currency || 'USD')}
                  </div>
                </div>
                <FontAwesomeIcon icon={faDollarSign} style={{ color: 'var(--gp-success)' }} />
              </div>
            </div>
            <div className="gp-stat text-left">
              <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={isRTL ? 'text-right' : 'text-left'}>
                  <div className="gp-stat-label">{t('billing.totalPurchases')}</div>
                  <div className="gp-stat-value">{totalPurchases}</div>
                </div>
                <FontAwesomeIcon icon={faReceipt} style={{ color: 'var(--gp-brand)' }} />
              </div>
            </div>
            <div className="gp-stat text-left">
              <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={isRTL ? 'text-right' : 'text-left'}>
                  <div className="gp-stat-label">{t('billing.activeSubscriptions')}</div>
                  <div className="gp-stat-value">{activeSubscriptions}</div>
                </div>
                <FontAwesomeIcon icon={faCheckCircle} style={{ color: 'var(--gp-success)' }} />
              </div>
            </div>
          </div>
        )}

        {billingHistory.length === 0 ? (
          <div className={`gp-card py-10 ${isRTL ? 'text-right' : 'text-center'}`}>
            <div className={`gp-icon-tile-lg mb-4 ${isRTL ? 'ml-auto' : 'mx-auto'}`}>
              <FontAwesomeIcon icon={faFileInvoice} className="text-xl" />
            </div>
            <h3 className="gp-heading-lg mb-2">{t('billing.noBillingHistory')}</h3>
            <p className="text-sm gp-text-muted mb-5">
              {t('billing.noBillingHistoryDescription')}
            </p>
            <Link
              to="/certifications"
              className={`gp-btn-primary inline-flex ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <span>{t('billing.browseCertifications')}</span>
              <FontAwesomeIcon icon={faArrowRight} className="text-xs" style={{ transform: isRTL ? 'scaleX(-1)' : 'none' }} />
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {billingHistory.map((record) => {
              const now = new Date();
              const endDate = new Date(record.end_date);
              const isExpired = endDate < now;
              const isActive = record.is_active && endDate > now;
              const daysRemaining = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

              return (
                <div
                  key={record.id}
                  className="gp-card"
                  style={{
                    borderColor: isActive
                      ? 'var(--gp-success)'
                      : isExpired
                      ? 'var(--gp-border)'
                      : 'var(--gp-brand)',
                  }}
                >
                  <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                      <div className="flex items-start gap-3 mb-3">
                        <div
                          className="gp-icon-tile-lg"
                          style={
                            isActive
                              ? { background: 'var(--gp-success)' }
                              : isExpired
                              ? { background: 'var(--gp-ink-subtle)' }
                              : undefined
                          }
                        >
                          <FontAwesomeIcon icon={faGraduationCap} className="text-lg" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 className="text-sm font-bold truncate" style={{ color: 'var(--gp-ink)' }}>
                              {record.exam_name}
                            </h3>
                            {getStatusBadge(record)}
                          </div>
                          {record.exam_description && (
                            <p className="text-xs gp-text-muted line-clamp-2 mb-2">
                              {record.exam_description}
                            </p>
                          )}
                          {isActive && daysRemaining > 0 && (
                            <div
                              className={`flex items-center gap-1 text-xs font-medium ${isRTL ? 'flex-row-reverse' : ''}`}
                              style={{ color: 'var(--gp-success)' }}
                            >
                              <FontAwesomeIcon icon={faClock} className="text-xs" />
                              <span>
                                {daysRemaining === 1
                                  ? t('billing.daysRemaining', { count: daysRemaining })
                                  : t('billing.daysRemainingPlural', { count: daysRemaining })}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                        <div className="p-3 rounded-md" style={{ background: 'var(--gp-surface-muted)', border: '1px solid var(--gp-border)' }}>
                          <div className={`flex items-center gap-2 mb-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <FontAwesomeIcon icon={faDollarSign} className="text-sm" style={{ color: 'var(--gp-success)' }} />
                            <p className={`text-[11px] font-medium uppercase tracking-wide gp-text-muted ${isRTL ? 'text-right' : 'text-left'}`}>
                              {t('billing.amountPaid')}
                            </p>
                          </div>
                          <p className={`text-base font-bold ${isRTL ? 'text-right' : 'text-left'}`} style={{ color: 'var(--gp-ink)' }}>
                            {formatCurrency(record.amount_paid || 0, record.currency || 'USD')}
                          </p>
                        </div>

                        <div className="p-3 rounded-md" style={{ background: 'var(--gp-surface-muted)', border: '1px solid var(--gp-border)' }}>
                          <div className={`flex items-center gap-2 mb-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <FontAwesomeIcon icon={faCalendar} className="text-sm" style={{ color: 'var(--gp-brand)' }} />
                            <p className={`text-[11px] font-medium uppercase tracking-wide gp-text-muted ${isRTL ? 'text-right' : 'text-left'}`}>
                              {t('billing.purchaseDate')}
                            </p>
                          </div>
                          <p className={`text-sm font-semibold ${isRTL ? 'text-right' : 'text-left'}`} style={{ color: 'var(--gp-ink)' }}>
                            {formatDate(record.created_at)}
                          </p>
                        </div>

                        <div className="p-3 rounded-md" style={{ background: 'var(--gp-surface-muted)', border: '1px solid var(--gp-border)' }}>
                          <div className={`flex items-center gap-2 mb-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <FontAwesomeIcon icon={faClock} className="text-sm" style={{ color: 'var(--gp-accent)' }} />
                            <p className={`text-[11px] font-medium uppercase tracking-wide gp-text-muted ${isRTL ? 'text-right' : 'text-left'}`}>
                              {t('billing.validUntil')}
                            </p>
                          </div>
                          <p className={`text-sm font-semibold ${isRTL ? 'text-right' : 'text-left'}`} style={{ color: 'var(--gp-ink)' }}>
                            {formatDate(record.end_date)}
                          </p>
                        </div>
                      </div>

                      {record.payment_id && (
                        <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--gp-border)' }}>
                          <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <FontAwesomeIcon icon={faIdCard} className="text-xs gp-text-muted" />
                            <p className={`text-xs gp-text-muted ${isRTL ? 'text-right' : 'text-left'}`}>
                              {t('billing.paymentId')}:{' '}
                              <span className="font-mono text-xs font-semibold" style={{ color: 'var(--gp-ink)' }}>
                                {record.payment_id}
                              </span>
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className={`flex md:flex-col items-center ${isRTL ? 'md:items-start' : 'md:items-end'} justify-between md:justify-start`}>
                      <Link
                        to={getExamUrl(record.exam_id, record.exam_name)}
                        className={`gp-link inline-flex items-center gap-2 text-sm ${isRTL ? 'flex-row-reverse' : ''}`}
                      >
                        <span>{t('billing.viewExam')}</span>
                        <FontAwesomeIcon icon={faArrowRight} className="text-xs" style={{ transform: isRTL ? 'scaleX(-1)' : 'none' }} />
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingHistory;
